import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import PublicNavbar from "../components/layout/PublicNavbar";
import Footer from "../components/layout/Footer";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import Seo from "../components/Seo";
import Price from "../components/common/Price";
import PaymentProviderPicker from "../components/payments/PaymentProviderPicker";

export default function Pricing() {
  const [audience, setAudience] = useState("startup");
  const [plans, setPlans] = useState([]);
  const { user } = useAuth();
  const nav = useNavigate();
  const [pickerCtx, setPickerCtx] = useState(null); // { appOrderId, cashfreeSessionId, amountInr, buyer }

  // Auto-select investor tab for signed-in investors on first mount
  useEffect(() => {
    if (user?.role === "investor") setAudience("investor");
  }, [user?.role]);

  useEffect(() => {
    api.get("/plans", { params: { audience } }).then((r) => setPlans(r.data || [])).catch(() => setPlans([]));
  }, [audience]);

  const upgrade = async (plan) => {
    if (!user) {
      toast.info(`Please sign in as ${audience === "investor" ? "an investor" : "a startup"} to upgrade.`);
      nav(`/signup?role=${audience}`);
      return;
    }
    if (user.role !== audience && !user.role?.includes("admin")) {
      toast.error(`This plan is for ${audience}s. You are signed in as ${user.role}.`);
      return;
    }
    if (plan.price_inr === 0) {
      toast.success(`You're on the ${plan.name} tier — no upgrade needed.`);
      return;
    }
    try {
      const { data } = await api.post("/payments/create-order", { plan_id: plan.plan_id, amount: plan.price_inr });
      if (data.status === "PLACEHOLDER" && !data.payment_session_id && !data.order_id) {
        toast.info("Payments not yet configured. Your upgrade intent has been recorded — admin will finalise.");
        return;
      }
      // Hand off to the provider picker (it auto-starts when only one
      // provider is enabled, and shows a chooser when both are live).
      setPickerCtx({
        appOrderId: data.order_id || data.app_order_id,
        cashfreeSessionId: data.payment_session_id,
        amountInr: plan.price_inr,
        buyer: {
          name: user.name || user.email,
          email: user.email,
          contact: user.phone || "",
        },
      });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen text-ink">
      <Seo
        title="Pricing & Membership Plans | VenturaZ"
        description="Transparent pricing for startups and investors. Unlock priority review, featured listings, curated deal flow and premium tools on VenturaZ."
        path="/pricing"
      />
      <Toaster richColors position="top-right" />
      <PublicNavbar />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="overline mb-3">MEMBERSHIP · PLACEHOLDER PRICING</div>
        <h1 className="font-display text-4xl lg:text-6xl text-ink leading-tight max-w-3xl">Plans built for serious capital.</h1>
        <p className="text-ink-soft mt-4 max-w-2xl">
          {audience === "startup"
            ? "Choose a startup membership to unlock priority review, featured listings and access to institutional investor tiers."
            : "Investor tiers unlock deeper deal flow, priority curation and access to institutional-grade insights."}
        </p>

        {/* Audience toggle */}
        <div className="mt-8 inline-flex glass rounded-full p-1" data-testid="pricing-audience-toggle">
          <button
            onClick={() => setAudience("startup")}
            data-testid="pricing-toggle-startup"
            className={`px-5 py-2 text-sm font-heading font-semibold rounded-full transition-all ${audience === "startup" ? "bg-navy text-white shadow-[0_4px_12px_rgba(15,61,46,0.2)]" : "text-ink-soft hover:text-ink"}`}
          >
            For Startups
          </button>
          <button
            onClick={() => setAudience("investor")}
            data-testid="pricing-toggle-investor"
            className={`px-5 py-2 text-sm font-heading font-semibold rounded-full transition-all ${audience === "investor" ? "bg-navy text-white shadow-[0_4px_12px_rgba(15,61,46,0.2)]" : "text-ink-soft hover:text-ink"}`}
          >
            For Investors
          </button>
        </div>

        <div className="hairline my-10" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid={`plans-grid-${audience}`}>
          {plans.length === 0
            ? <div className="col-span-full text-ink-muted text-sm">No {audience} plans available at the moment.</div>
            : plans.map((p) => <PricingCard key={p.plan_id} plan={p} onUpgrade={() => upgrade(p)} />)}
        </div>
      </section>
      <Footer />
      <PaymentProviderPicker
        open={!!pickerCtx}
        onClose={() => setPickerCtx(null)}
        appOrderId={pickerCtx?.appOrderId}
        cashfreeSessionId={pickerCtx?.cashfreeSessionId}
        amountInr={pickerCtx?.amountInr}
        buyer={pickerCtx?.buyer}
        onSuccess={() => setPickerCtx(null)}
      />
    </div>
  );
}

function PricingCard({ plan, onUpgrade }) {
  return (
    <div className={`${plan.featured ? "glass-gold" : "glass"} rounded-lg p-6`} data-testid={`plan-card-${plan.plan_id}`}>
      {plan.featured && <div className="text-[10px] font-mono tracking-widest text-gold mb-2">RECOMMENDED</div>}
      <div className="font-heading text-ink text-xl">{plan.name}</div>
      <div className="text-sm text-ink-muted mt-1">{plan.tagline}</div>
      <div className="mt-6">
        {plan.price_inr === 0 ? (
          <div className="font-display text-3xl text-ink">Free</div>
        ) : (
          <Price
            price={plan.price_inr}
            originalPrice={plan.original_price_inr}
            size="lg"
            suffix="/ quarter"
            testId={`plan-price-${plan.plan_id}`}
          />
        )}
      </div>
      <ul className="mt-6 space-y-2 text-sm text-ink-soft">
        {plan.features?.map((f, i) => <li key={i} className="flex gap-2"><span className="text-gold">→</span> {f}</li>)}
      </ul>
      <button
        onClick={onUpgrade}
        className={`mt-8 block w-full text-center py-3 text-sm font-heading font-semibold transition-all rounded-md ${plan.featured ? "bg-gold text-navy hover:bg-gold-hover shadow-[0_8px_24px_rgba(255,215,0,0.25)]" : "bg-navy text-white hover:bg-[#256E58] shadow-[0_8px_24px_rgba(15,61,46,0.18)]"}`}
        data-testid={`plan-cta-${plan.plan_id}`}
      >
        {plan.price_inr === 0 ? "Get Started" : "Upgrade Now"}
      </button>
    </div>
  );
}
